/**
 * Display derivations for a single column header cell. These are all questions about a column, so
 * they take the column's own metadata rather than an axis and an index.
 */

import {isColumnOutdated, ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import columnDefinitionToIcon from 'notebook/structured-tables/columns/columnDefinitionToIcon';
import {
  StructuredTableMappingsGlobalState,
  StructuredTableMappingsStateManager,
} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsState';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {ColumnConstraint, TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * Whether the column at `index` is one of the leading Single Input columns, which are rendered by
 * `TableSingleInput` rather than by the column header row.
 */
export function computeIsSingleInputColumn(index: number, singleInputBoundaryIndex: number | null): boolean {
  return singleInputBoundaryIndex != null && index < singleInputBoundaryIndex;
}

/**
 * Whether the column header should be highlighted as a user-added custom column. Single Input
 * headers are never highlighted.
 */
export function computeShouldHighlightColumnHeader({
  isSingleInputColumn,
  highlightCustomColumns,
  columnDefinition,
  tableKind,
}: {
  isSingleInputColumn: boolean;
  highlightCustomColumns: boolean | undefined;
  columnDefinition: ColumnDefinition | null;
  tableKind: StructuredTableKind | null;
}): boolean {
  if (isSingleInputColumn) {
    return false;
  }
  return (
    Boolean(highlightCustomColumns) &&
    columnDefinition?.type === TableColumnType.CUSTOM &&
    // Custom Columns should not be highlighted if they have an assigned column group (indicating they have
    // a specific role and were not just added by the user)
    columnDefinition?.columnGroup == null &&
    // Parameter tables use custom cols for datetime values, but these should not be highlighted
    tableKind !== StructuredTableKind.PARAMETER
  );
}

/**
 * Whether the given column is a destination column for at least one table mapping. This reads the
 * global table mappings state, so it must be a selector input rather than a memoized result.
 */
export function computeHasDestinationColumnMapping(
  fileId: string,
  table: Table,
  columnConstraint: ColumnConstraint | null
): boolean {
  const tableId = table.provenanceUuid;
  const columnId = columnConstraint?.structuredTableColumn?.provenanceUuid;

  if (tableId == null || columnId == null) {
    return false;
  }

  const structuredTableMappingsState = StructuredTableMappingsGlobalState.getState(fileId);
  const mappingsForColumn = StructuredTableMappingsStateManager.getMappingsForDestinationColumn(
    structuredTableMappingsState,
    tableId,
    columnId
  );
  return Boolean(mappingsForColumn.length);
}

/**
 * The icon rendered before the column header's label: the column mapping icon for a mapped
 * destination column, otherwise the column type icon. Tables that hide column header icons have no
 * prefix icon.
 */
export function computeColumnHeaderIconPrefix({
  hideColumnHeaderIcons,
  hasDestinationColumnMapping,
  columnDefinition,
}: {
  hideColumnHeaderIcons: boolean | undefined;
  hasDestinationColumnMapping: boolean;
  columnDefinition: ColumnDefinition | null;
}): BenchlingIconName | null {
  if (hideColumnHeaderIcons) {
    return null;
  }
  if (hasDestinationColumnMapping) {
    return 'column-mapping';
  }
  if (columnDefinition) {
    return columnDefinitionToIcon(columnDefinition);
  }
  return null;
}

/** Whether the column is backed by an outdated schema field. */
export function computeColumnIsOutdated(columnDefinition: ColumnDefinition | null): boolean {
  return isColumnOutdated(columnDefinition);
}
